import React, { useState, useRef, useEffect } from "react";
import "./CustomerCertificates.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { fetchUserProfile } from "../../redux/slices/branch/userProfileSlice";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

const CustomerCertificates = () => {
  const componentRef = useRef();
  const [company, setCompany] = useState(null);
  const { profile } = useSelector((state) => state.userProfile);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const hostname = window.location.hostname;
  const parts = hostname.split(".");
  const mainDomain = parts.slice(-2).join(".");
  const [customerPhoto, setCustomerPhoto] = useState(null);

  const selectedCustomer = location.state?.row || null;

  const generatePDF = async () => {
    if (!componentRef.current) return;

    const canvas = await html2canvas(componentRef.current, {
      scale: 2, 
      useCORS: true,
    });

    const imgData = canvas.toDataURL("image/jpeg", 0.75); 
    
    const pdf = new jsPDF("p", "pt", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    
    const margin = 20; 
    const printableWidth = pdfWidth - (margin * 2);
    const printableHeight = (canvas.height * printableWidth) / canvas.width;

    pdf.addImage(imgData, "JPEG", margin, margin, printableWidth, printableHeight, undefined, 'FAST');
    pdf.save(`${selectedCustomer?.fullName || "Customer"}_CustomerForm.pdf`);
  };

  const handlePrint = () => {
    if (!componentRef.current) return;

    const printWindow = window.open("", "_blank", "width=900,height=650");

    printWindow.document.write(`
      <html>
        <head>
          <title>Customer Application Form</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              background-color: #fff;
              color: #000;
            }
            .Customer-Certificates-wrapper {
              width: 100%;
              border: 2px solid #000 !important;
              padding: 20px;
              box-sizing: border-box;
            }
            .Customer-Certificates-Branch-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
              margin-bottom: 15px;
            }
            .Customer-Certificates-Branch-logo-box {
              width: 80px;
              height: 80px;
            }
            .Customer-Certificates-Branch-logo {
              width: 100%;
              height: 100%;
              object-fit: contain;
            }
            .Customer-Certificates-Branch-company-details {
              text-align: right;
              flex: 1;
            }
            .Customer-Certificates-Branch-company-name {
              font-size: 20px;
              font-weight: bold;
              margin: 0 0 5px 0;
            }
            .Customer-Certificates-Branch-company-details p {
              font-size: 13px;
              margin: 0;
            }
            .Customer-Certificates-Branch-title {
              text-align: center;
              font-size: 22px;
              font-weight: bold;
              text-transform: uppercase;
              margin-bottom: 15px;
            }
            .Customer-Certificates-title, .CustomerCertificatessection-title {
              background: #eaeaea;
              color: #003366;
              font-weight: bold;
              padding: 4px 8px;
              margin-bottom: 6px;
              border: 1px solid #999;
              text-align: left;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
              font-size: 15px;
            }
            table td {
              border: 1px solid #000 !important;
              padding: 6px 8px;
              vertical-align: top;
            }
            .Customer-Certificates-box {
              width: 140px;
              text-align: center;
            }
            .Customer-Certificates-photo {
              width: 120px;
              height: 120px;
              object-fit: contain;
              border: 1px solid #000;
            }
            .Customer-Certificates-placeholder {
              width: 120px;
              height: 120px;
              border: 1px dashed #000;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 13px;
            }
            .CustomerCertificatessign-section {
              margin-top: 40px;
              display: flex;
              justify-content: space-between;
              font-weight: bold;
            }
          </style>
        </head>
        <body>
          <div class="Customer-Certificates-wrapper">
            ${componentRef.current.innerHTML}
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  };

  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  useEffect(() => {
    const fetchAllData = async () => {
      if (!profile?.superAdminId) return;

      try {
        const res = await fetch(
          `https://payyug.in/api/get-company?superAdminId=${profile.superAdminId}`
        );
        const companyData = await res.json();

        if (res.ok) {
          if (companyData.logo) {
            companyData.logoUrl = `https://payyug.in/${companyData.logo.replace(
              /^images\//,
              ""
            )}`;
          }
          setCompany(companyData);
        }
      } catch (err) {
        console.error("Company fetch error:", err);
      }
    };

    fetchAllData();
  }, [profile?.superAdminId]);

  useEffect(() => {
    const fetchCustomerPhoto = async () => {
      if (!selectedCustomer?.userId) return;
      try {
        const res = await fetch(
          `https://payyug.in/api/list-kyc?userId=${selectedCustomer.userId}&superAdminId=${profile?.superAdminId}&createdId=${profile.id}`
        );
        if (!res.ok) return;
        const data = await res.json();
        const photoDoc = data?.documents?.find(
          (doc) => doc?.docName?.toLowerCase() === "photo"
        );
        if (photoDoc?.docImage) {
          setCustomerPhoto(`https://payyug.in/uploads/${photoDoc.docImage}`);
        }
      } catch (err) {
        console.error("Photo fetch error:", err);
      }
    };

    fetchCustomerPhoto();
  }, [profile?.superAdminId, selectedCustomer?.userId, profile?.id]);

  const renderDate = (dateString) => {
    if (!dateString) return "N/A";
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? "N/A" : d.toLocaleDateString("en-GB");
  };

  if (!selectedCustomer) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <p>No customer data found</p>
        <button onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  return (
    <div>
      <div className="CustomerCertificates-print-btn-container">
        <button className="CustomerCertificatesprint-btn" onClick={generatePDF}>
          ⬇ Download PDF
        </button>
        <button className="CustomerCertificatesprint-btn print-only-btn" onClick={handlePrint}>
          🖨 Print Form
        </button>
      </div>

      <div className="Customer-Certificates-wrapper" ref={componentRef}>
        <div className="Customer-Certificates-Branch-header">
          <div className="Customer-Certificates-Branch-logo-box">
            {company?.logoUrl && (
              <img
                src={company.logoUrl}
                alt="logo"
                className="Customer-Certificates-Branch-logo"
                crossOrigin="anonymous"
              />
            )}
          </div>

          <div className="Customer-Certificates-Branch-company-details">
            <h2 className="Customer-Certificates-Branch-company-name">
              {company?.name || "Techvision"}
            </h2>
            <p><b>Address:</b> {company?.address || "-"}</p>
            <p><b>CIN:</b> {company?.cin || "-"}</p>
            <p><b>Mobile:</b> {company?.contact || "-"}</p>
            <p><b>Email:</b> {company?.email || "-"}</p>
            <p><b>Website:</b> {mainDomain || "-"}</p>
          </div>
        </div>

        <h2 className="Customer-Certificates-Branch-title">
          Customer Application Form
        </h2>

        <h3 className="Customer-Certificates-title">PERSONAL INFORMATION</h3>
        <table className="Customer-Certificates-table">
          <tbody>
            <tr>
              <td><strong>Name :</strong> {selectedCustomer.fullName || "N/A"}</td>
              <td><strong>Pan Number :</strong> {selectedCustomer.panNumber || "N/A"}</td>
              <td rowSpan={3} className="Customer-Certificates-box">
                {customerPhoto ? (
                  <img
                    src={customerPhoto}
                    alt="Customer"
                    className="Customer-Certificates-photo"
                    crossOrigin="anonymous"
                  />
                ) : (
                  <div className="Customer-Certificates-placeholder">No Photo</div>
                )}
              </td>
            </tr>
            <tr>
              <td><strong>Aadhaar Number :</strong> {selectedCustomer.aadhaarNumber || "N/A"}</td>
              <td>
                <strong>Title :</strong>{" "}
                {selectedCustomer?.fullName && selectedCustomer.fullName.trim().split(" ").length > 1
                  ? selectedCustomer.fullName.trim().split(" ").slice(-1)[0]
                  : "N/A"}
              </td>
            </tr>
            <tr>
              <td><strong>Gender :</strong> {selectedCustomer.gender || "N/A"}</td>
              <td><strong>DOB :</strong> {renderDate(selectedCustomer.dateOfBirth)}</td>
            </tr>
          </tbody>
        </table>

        <h3 className="CustomerCertificatessection-title">Contact & Banking</h3>
        <table className="CustomerCertificatesinfo-table">
          <tbody>
            <tr>
              <td><strong>Full Name :</strong> {selectedCustomer.fullName || "N/A"}</td>
              <td><strong>Guardian/Father/husband/Care of Name :</strong> {selectedCustomer.guardianName || "N/A"}</td>
              <td><strong>Mobile Number :</strong> {selectedCustomer.mobileNumber || "N/A"}</td>
              <td><strong>Email ID :</strong> {selectedCustomer.email || "N/A"}</td>
            </tr>
            <tr>
              <td><strong>Pin Code :</strong> {selectedCustomer.pincode || "N/A"}</td>
              <td><strong>Post Office :</strong> {selectedCustomer.postOffice || "N/A"}</td>
              <td><strong>Bank Account :</strong> {selectedCustomer.bankAccount || "N/A"}</td>
              <td><strong>IFSC :</strong> {selectedCustomer.bankIFSC || "N/A"}</td>
            </tr>
            <tr>
              <td colSpan={2}><strong>Bank Branch :</strong> {selectedCustomer.bankBranch || "N/A"}</td>
              <td colSpan={2}><strong>Bank Name :</strong> {selectedCustomer.bankName || "N/A"}</td>
            </tr>
          </tbody>
        </table>

        <h3 className="CustomerCertificatessection-title">Address</h3>
        <table className="CustomerCertificatesinfo-table">
          <tbody>
            <tr>
              <td><strong>Sub District :</strong> {selectedCustomer.subDistrict || "N/A"}</td>
              <td><strong>District :</strong> {selectedCustomer.district || "N/A"}</td>
              <td><strong>State :</strong> {selectedCustomer.state || "N/A"}</td>
              <td><strong>Country :</strong> {selectedCustomer.country || "N/A"}</td>
            </tr>
            <tr>
              <td colSpan={2}><strong>Current Address :</strong> {selectedCustomer.address || "N/A"}</td>
              <td colSpan={2}><strong>Permanent Address :</strong> {selectedCustomer.address || "N/A"}</td>
            </tr>
          </tbody>
        </table>

        <h3 className="CustomerCertificatessection-title">Other Details</h3>
        <table className="CustomerCertificatesinfo-table">
          <tbody>
            <tr>
              <td><strong>Marital Status :</strong> {selectedCustomer.maritalStatus || "N/A"}</td>
              <td><strong>Date of Joining :</strong> {renderDate(selectedCustomer.dateOfJoining)}</td>
            </tr>
          </tbody>
        </table>

        {/* SIGNATURES */}
        <div className="CustomerCertificatessign-section">
          <p>Signature of Applicant: __________________________</p>
          <p>Signature of Authorize: __________________________</p>
        </div>
      </div>
    </div>
  );
};

export default CustomerCertificates;